import { createContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { I18nProvider as LinguiProvider } from '@lingui/react';
import { i18n, activateLocale, preloadBundledLocales, type SupportedLocale } from '@pantry/i18n';
import { setApiLocale, useCurrentUser, queryKeys } from '@pantry/api-client';
import { useSelectedHousehold } from './HouseholdContext';

interface I18nContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => Promise<void>;
  isLoading: boolean;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const { data: user } = useCurrentUser();
  const { selectedHousehold } = useSelectedHousehold();
  const queryClient = useQueryClient();
  const [locale, setLocaleState] = useState<SupportedLocale>('en');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Initialize locale with precedence: user > household > browser
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        // First preload all bundled locales for instant switching
        await preloadBundledLocales();

        if (cancelled) return;

        let targetLocale: string | null = null;

        // 1. User profile language
        if (user?.language) {
          targetLocale = user.language;
        }

        // 2. Household language
        if (!targetLocale && selectedHousehold?.language) {
          targetLocale = selectedHousehold.language;
        }

        // 3. Browser language
        if (!targetLocale && typeof navigator !== 'undefined') {
          targetLocale = navigator.language;
        }

        const normalized = await activateLocale(targetLocale || 'en');

        if (cancelled) return;

        setApiLocale(normalized);
        document.documentElement.lang = normalized;
        setLocaleState(normalized);
        setIsLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to load translations'));
          setIsLoading(false);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [user?.language, selectedHousehold?.language]);

  const setLocale = useCallback(
    async (newLocale: SupportedLocale) => {
      setIsLoading(true);
      const normalized = await activateLocale(newLocale);
      setApiLocale(normalized);
      document.documentElement.lang = normalized;

      // Invalidate queries that return locale-dependent content
      await queryClient.invalidateQueries({ queryKey: queryKeys.announcements.active() });

      setLocaleState(normalized);
      setIsLoading(false);
    },
    [queryClient]
  );

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load translations</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <I18nContext.Provider value={{ locale, setLocale, isLoading }}>
      <LinguiProvider i18n={i18n}>{children}</LinguiProvider>
    </I18nContext.Provider>
  );
}
